// Apolo IDE — preview toolbar (sprint code-preview-engine, E2-WS2-T01).
// The browser chrome: run controls + status + URL (searchbar) + open-external + element picker +
// close, with the device presets below. (Pedro) the Console/Network/Inspect tabs and the screenshot
// camera were removed — the panel is a clean Arc/Dia-style live preview; the URL bar lives in the
// header next to the controls. Minimalist: neutral hover, color only on the status badge / active device.

import { useEffect, useRef, useState } from 'react';
import {
  Play, Square, RotateCw, ExternalLink, X,
  Maximize2, Monitor, Tablet, Smartphone, ChevronDown, Check,
  MousePointer2, ListTodo,
} from 'lucide-react';
import { DEVICE_ORDER, DEVICE_PRESETS } from './DevicePresets.js';

// 2026-07-10 (Pedro) — decisão PURA do menu do pointer: UM ícone, DOIS modos
// mutuamente exclusivos. Escolher um modo liga ele (e desliga o outro); escolher o
// modo já ativo desliga (toggle). Devolve quais toggles disparar — testável sem DOM.
//
// 2026-07-11 FIX: togglar SÓ o modo escolhido. A exclusão mútua já mora nos toggles
// do store (#608: toggleAnnotateMode desliga design ao LIGAR, e vice-versa). Togglar
// TAMBÉM o irmão duplicava a exclusão e as duas se CANCELAVAM → "Anotar vários" (vindo
// do design ligado) não fazia nada. Como o store é a autoridade (o InspectTab também
// chama toggleDesignMode direto e depende dela), aqui só disparamos 1 toggle.
export function decidePointerToggles(mode) {
  return { toggleDesign: mode === 'single', toggleAnnotate: mode === 'annotate' };
}

// Dropdown do pointer (padrão do ViewportDropdown): "Selecionar 1" (Design Mode →
// pill #489) / "Anotar vários" (badges numerados #608). Visualmente é 1 pointer.
function PointerDropdown({ designModeOn, toggleDesignMode, annotateModeOn, toggleAnnotateMode, onOpenChange }) {
  const [open, setOpenRaw] = useState(false);
  // BrowserView é NATIVA e engole HTML por cima (#608) — enquanto o menu está
  // aberto o caller PAUSA o guest (PreviewViewport.paused) pra ele aparecer.
  const setOpen = (v) => setOpenRaw((prev) => {
    const next = typeof v === 'function' ? v(prev) : v;
    if (next !== prev) onOpenChange?.(next);
    return next;
  });
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);
  const anyOn = !!designModeOn || !!annotateModeOn;
  const pick = (mode) => {
    const t = decidePointerToggles(mode);
    if (t.toggleAnnotate) toggleAnnotateMode?.();
    if (t.toggleDesign) toggleDesignMode?.();
    setOpen(false);
  };
  // 2026-07-11 — o ícone pelado era INVISÍVEL (Pedro não achou): agora é um botão
  // ROTULADO no padrão do ViewportDropdown (ícone + texto + seta). O texto muda pro
  // modo ativo pra ficar óbvio o que está ligado.
  const triggerLabel = designModeOn ? 'Selecionando 1' : annotateModeOn ? 'Anotando' : 'Selecionar';
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-pressed={anyOn}
        title="Selecionar elemento(s) na página"
        data-testid="pointer-dropdown-trigger"
        className={`inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md px-2 text-[12px] font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40 ${
          anyOn ? 'bg-apolo-accent/10 text-apolo-accent' : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
        }`}
      >
        <MousePointer2 size={14} aria-hidden="true" />
        <span>{triggerLabel}</span>
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 top-full z-30 mt-1 min-w-[196px] rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-1 shadow-lg">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={!!designModeOn}
            data-testid="pointer-mode-single"
            onClick={() => pick('single')}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] transition ${designModeOn ? 'bg-apolo-accent/10 text-apolo-accent' : 'text-apolo-textSecondary hover:bg-apolo-surface3 hover:text-apolo-textPrimary'}`}
          >
            <MousePointer2 size={14} aria-hidden="true" />
            <span className="flex-1">Selecionar 1 elemento</span>
            {designModeOn ? <Check size={13} aria-hidden="true" /> : null}
          </button>
          <button
            type="button"
            role="menuitemradio"
            aria-checked={!!annotateModeOn}
            data-testid="pointer-mode-annotate"
            onClick={() => pick('annotate')}
            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] transition ${annotateModeOn ? 'bg-apolo-accent/10 text-apolo-accent' : 'text-apolo-textSecondary hover:bg-apolo-surface3 hover:text-apolo-textPrimary'}`}
          >
            <ListTodo size={14} aria-hidden="true" />
            <span className="flex-1">Anotar vários</span>
            {annotateModeOn ? <Check size={13} aria-hidden="true" /> : null}
          </button>
        </div>
      )}
    </div>
  );
}

const DEVICE_ICONS = { responsive: Maximize2, desktop: Monitor, tablet: Tablet, mobile: Smartphone };
// Pedro-facing labels for the viewport dropdown (icon + nome).
const DEVICE_LABELS = { responsive: 'Expandir', desktop: 'Desktop', tablet: 'Tablet', mobile: 'Celular' };

const STATUS = {
  idle: { label: 'parado', cls: 'text-apolo-textTertiary' },
  starting: { label: 'subindo', cls: 'text-apolo-warning' },
  ready: { label: 'ao vivo', cls: 'text-apolo-success' },
  crashed: { label: 'erro', cls: 'text-apolo-error' },
  no_script: { label: 'manual', cls: 'text-apolo-textTertiary' },
  stopped: { label: 'parado', cls: 'text-apolo-textTertiary' },
  unsupported: { label: 'web', cls: 'text-apolo-textTertiary' },
};

function IconButton({ icon: Icon, label, onClick, active = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40 disabled:opacity-40 ${
        active
          ? 'bg-apolo-accent/10 text-apolo-accent'
          : 'text-apolo-textTertiary hover:bg-apolo-surface2 hover:text-apolo-textPrimary'
      }`}
    >
      <Icon size={14} aria-hidden="true" />
    </button>
  );
}

// Viewport picker collapsed into a single dropdown (Pedro): the current device's
// icon + name, opening a menu of icon+label rows, plus an "open in a new window"
// action. Replaces the row of loose icon buttons.
function ViewportDropdown({ device, setDevice, url }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);
  const Current = DEVICE_ICONS[device] || Monitor;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Tamanho de tela"
        className="inline-flex h-6 items-center gap-1.5 rounded-md px-2 text-[11px] text-apolo-textSecondary transition hover:bg-apolo-surface2 hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
      >
        <Current size={13} aria-hidden="true" />
        <span>{DEVICE_LABELS[device] || 'Tela'}</span>
        <ChevronDown size={12} aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-full z-30 mt-1 min-w-[172px] rounded-lg border border-apolo-borderSubtle bg-apolo-surface2 p-1 shadow-lg">
          {DEVICE_ORDER.map((key) => {
            const Icon = DEVICE_ICONS[key];
            const active = device === key;
            return (
              <button
                key={key}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => { setDevice?.(key); setOpen(false); }}
                className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] transition ${active ? 'bg-apolo-accent/10 text-apolo-accent' : 'text-apolo-textSecondary hover:bg-apolo-surface3 hover:text-apolo-textPrimary'}`}
              >
                <Icon size={14} aria-hidden="true" />
                <span className="flex-1">{DEVICE_LABELS[key]}</span>
                {DEVICE_PRESETS[key]?.width ? (
                  <span className="text-[10px] tabular-nums text-apolo-textTertiary">{DEVICE_PRESETS[key].width}×{DEVICE_PRESETS[key].height}</span>
                ) : null}
                {active ? <Check size={13} aria-hidden="true" /> : null}
              </button>
            );
          })}
          {url ? (
            <>
              <div className="my-1 h-px bg-apolo-borderSubtle" aria-hidden="true" />
              <button
                type="button"
                role="menuitem"
                onClick={() => { window.open(url, '_blank', 'noopener'); setOpen(false); }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-apolo-textSecondary transition hover:bg-apolo-surface3 hover:text-apolo-textPrimary"
              >
                <ExternalLink size={14} aria-hidden="true" />
                <span>Abrir em nova janela</span>
              </button>
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default function PreviewToolbar({
  state = 'idle',
  url,
  supported = true,
  manualUrl = '',
  setManualUrl,
  commitUrl,
  device = 'desktop',
  setDevice,
  annotateModeOn = false,
  toggleAnnotateMode,
  designModeOn = false,
  toggleDesignMode,
  onPointerMenuOpenChange,
  onStart,
  onStop,
  onRestart,
  onClose,
}) {
  const running = state === 'ready' || state === 'starting';
  const status = STATUS[state] || STATUS.idle;

  return (
    <div className="flex shrink-0 flex-col gap-2 border-b border-apolo-borderSubtle px-2 py-2">
      {/* Row 1: run + status + url + actions */}
      <div className="flex items-center gap-1.5">
        {supported ? (
          running ? (
            <>
              <IconButton icon={Square} label="Parar dev server" onClick={onStop} />
              <IconButton icon={RotateCw} label="Reiniciar dev server" onClick={onRestart} />
            </>
          ) : (
            <button
              type="button"
              onClick={onStart}
              className="inline-flex h-7 items-center gap-1.5 rounded-md bg-apolo-accent/10 px-2 text-[12px] font-medium text-apolo-accent transition hover:bg-apolo-accent/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/40"
            >
              <Play size={13} aria-hidden="true" />
              Iniciar
            </button>
          )
        ) : null}

        <span
          className={`shrink-0 font-mono text-[10px] uppercase tracking-wider ${status.cls}`}
          data-testid="preview-status"
        >
          {status.label}
        </span>

        {/* URL always editable (Electron too): the detected dev-server URL
            auto-fills it, but the user can paste any running localhost URL as a
            reliable fallback when auto-start doesn't fit the project. */}
        <input
          type="text"
          value={manualUrl}
          onChange={(e) => setManualUrl?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitUrl?.();
            }
          }}
          onBlur={() => commitUrl?.()}
          placeholder="Pesquise ou digite uma URL"
          aria-label="URL do preview (Enter para navegar)"
          className="min-w-0 flex-1 rounded-lg border border-apolo-borderSubtle bg-apolo-bg px-3 py-1.5 text-[12px] text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:border-apolo-borderStrong focus:outline-none"
        />

        {url ? (
          <IconButton
            icon={ExternalLink}
            label="Abrir em nova janela"
            onClick={() => window.open(url, '_blank', 'noopener')}
          />
        ) : null}
        {/* 2026-07-10 (Pedro) — UM único pointer com DOIS modos no dropdown:
            "Selecionar 1" (Design Mode → pill compacta #489) e "Anotar vários"
            (badges numerados #608). Exclusão mútua em decidePointerToggles. */}
        <PointerDropdown
          designModeOn={designModeOn}
          toggleDesignMode={toggleDesignMode}
          annotateModeOn={annotateModeOn}
          toggleAnnotateMode={toggleAnnotateMode}
          onOpenChange={onPointerMenuOpenChange}
        />
        {onClose ? (
          <IconButton icon={X} label="Fechar painel" onClick={onClose} />
        ) : null}
      </div>

      {/* Row 2: viewport dropdown (icon + nome) + abrir em nova janela */}
      <div className="flex items-center" role="group" aria-label="Tamanho de tela">
        <ViewportDropdown device={device} setDevice={setDevice} url={url} />
      </div>
    </div>
  );
}
